import { Button } from '@hologram-dimension/button';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useEmbeddedReport from 'api/legacy/reports/useEmbeddedReport';
import clsx from 'clsx/lite';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import styles from './EmbeddedReport.module.css';

interface EmbeddedReportProps {
  reportId: number;
  className?: string;
  simCardId?: SimCardId;
  hologramSimId?: HologramSimId;
  billingPeriodStart?: number;
  billingPeriodEnd?: number;
}

const EmbeddedReport: FC<EmbeddedReportProps> = ({
  reportId,
  className,
  simCardId,
  hologramSimId,
  billingPeriodStart,
  billingPeriodEnd,
}) => {
  const [loadingReport, setLoadingReport] = useState(true);
  const [reportHeight, setReportHeight] = useState(0);
  const orgId = useOrganization();

  const { embeddedReport, isLoading, error } = useEmbeddedReport(
    orgId,
    reportId,
    simCardId,
    hologramSimId,
    billingPeriodStart,
    billingPeriodEnd
  );

  const { name, url } = embeddedReport;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const handleIframeMessage = useCallback<any>((event: any) => {
    if (!iframeRef.current) {
      return;
    }

    if (
      event.source === iframeRef.current.contentWindow &&
      event.origin === 'https://app.sigmacomputing.com'
    ) {
      const { type, pageHeight } = event?.data ?? {};

      switch (type) {
        case 'workbook:loaded':
          setLoadingReport(false);
          break;

        case 'workbook:pageheight:onchange':
          setReportHeight(pageHeight);
          break;

        default:
          break;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);

    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  const contents = useMemo(() => {
    if (error) {
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading this report"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    }

    if (isLoading && !url) {
      return null;
    }

    return (
      <iframe
        title={name}
        src={url}
        className={styles.reportIframe}
        style={{
          display: loadingReport ? 'none' : undefined,
          height: reportHeight === 0 || loadingReport ? '100%' : `${reportHeight + 32}px`,
        }}
        ref={iframeRef}
      />
    );
  }, [error, isLoading, loadingReport, name, reportHeight, url]);

  return (
    <div className={clsx(styles.wrapper, loadingReport && styles.wrapperLoading, className)}>
      {loadingReport && <Loader className={styles.loader} />}
      {contents}
    </div>
  );
};

export default EmbeddedReport;
